package com.daytimegaming.ourdailyfinances.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.usecase.AuthUseCase
import com.daytimegaming.ourdailyfinances.domain.repository.UserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

import com.daytimegaming.ourdailyfinances.domain.fcm.FcmManager

class MainViewModel(
    private val authUseCase: AuthUseCase,
    private val userRepository: UserRepository,
    private val fcmManager: FcmManager,
) : ViewModel() {

    val currentUser = authUseCase.GetCurrentUser()
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5_000),
            initialValue = null,
        )

    private val _isAuthInitialized = MutableStateFlow(false)
    val isAuthInitialized = _isAuthInitialized.asStateFlow()

    private val _isProfileComplete = MutableStateFlow<Boolean?>(null)
    val isProfileComplete: StateFlow<Boolean?> = _isProfileComplete.asStateFlow()

    private val _userEmail = MutableStateFlow("")
    val userEmail: StateFlow<String> = _userEmail.asStateFlow()

    private val _isAppInitialized = MutableStateFlow(false)
    val isInitializing: StateFlow<Boolean> = _isAppInitialized
        .map { !it }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5_000),
            initialValue = true
        )

    init {
        fcmManager.initialize()

        // Observe currentUser to fetch profile when logged in, or reset when logged out
        viewModelScope.launch {
            authUseCase.GetCurrentUser().collect { user ->
                _isAuthInitialized.value = true
                if (user == null) {
                    _isProfileComplete.value = null
                    _userEmail.value = ""
                } else {
                    fetchProfile()
                }
            }
        }

        // Also observe userRepository.currentUserProfile to keep state in sync
        viewModelScope.launch {
            userRepository.currentUserProfile.collect { profile ->
                if (profile != null) {
                    val complete = !profile.firstName.isNullOrBlank() && !profile.lastName.isNullOrBlank()
                    _isProfileComplete.value = complete
                    _userEmail.value = profile.email
                }
            }
        }

        // Monitor initialization state
        viewModelScope.launch {
            combine(
                _isAuthInitialized,
                currentUser,
                isProfileComplete
            ) { authInit, user, profileComplete ->
                if (authInit) {
                    if (user == null) {
                        true
                    } else {
                        profileComplete != null
                    }
                } else {
                    false
                }
            }.collect { ready ->
                if (ready) {
                    _isAppInitialized.value = true
                }
            }
        }
    }

    private fun fetchProfile() {
        viewModelScope.launch {
            try {
                val profile = userRepository.getProfile()
                val complete = !profile.firstName.isNullOrBlank() && !profile.lastName.isNullOrBlank()
                _isProfileComplete.value = complete
                _userEmail.value = profile.email
            } catch (e: Exception) {
                // If it fails (e.g. 404), we assume user is not registered on backend yet
                _isProfileComplete.value = false
            }
        }
    }
}
